import { Injectable, Injector, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { AdminSessionProfileService } from '../services/admin-session-profile.service';
import { PermissionService } from '../services/permission.service';
import { RbacLiveSyncService } from '../services/rbac-live-sync.service';
import { PlatformNotificationService } from '../services/platform-notification.service';

/** Login/MFA step endpoints — 401 here means bad OTP or stale login, not an expired app session. */
export function isPublicAuthApiPath(url: string): boolean {
  const p = url.split('?')[0];
  return (
    p.includes('/auth/admin/login') ||
    p.includes('/auth/admin/verify-mfa') ||
    p.includes('/auth/admin/logout') ||
    p.includes('/auth/admin/forgot-password') ||
    p.includes('/auth/admin/create-password') ||
    p.includes('/auth/password-token')
  );
}

/** Detect auth/session failure from API envelope bodies (HTTP 200 + statusCode). */
export function isAuthFailureEnvelope(body: unknown): boolean {
  if (!body || typeof body !== 'object') return false;
  const b = body as { status?: boolean; statusCode?: number; message?: unknown };
  const code = typeof b.statusCode === 'number' ? b.statusCode : null;
  if (code === 401) return true;
  const msg = typeof b.message === 'string' ? b.message.toLowerCase() : '';
  if (code === 403 && /unauthorized|expired|invalid token|jwt|session/.test(msg)) return true;
  return b.status === false && code === 401;
}

export function isAuthFailureHttpError(error: HttpErrorResponse, requestUrl?: string): boolean {
  if (requestUrl && isPublicAuthApiPath(requestUrl)) return false;
  if (error.status === 401) return true;
  if (error.status === 403 && isAuthFailureEnvelope(error.error)) return true;
  if (isAuthFailureEnvelope(error.error)) return true;
  return false;
}

export function shouldRedirectOnAuthFailure(requestUrl: string, bodyOrError?: unknown): boolean {
  if (isPublicAuthApiPath(requestUrl)) return false;
  if (bodyOrError instanceof HttpErrorResponse) {
    return isAuthFailureHttpError(bodyOrError, requestUrl);
  }
  return isAuthFailureEnvelope(bodyOrError);
}

/**
 * Clears in-memory session state and navigates to sign-in (cookie session cleared by backend on logout).
 */
@Injectable({ providedIn: 'root' })
export class AuthSessionRedirectService {
  private readonly router = inject(Router);
  private readonly profile = inject(AdminSessionProfileService);
  private readonly permissionService = inject(PermissionService);

  /**
   * Resolved lazily inside `redirectToLogin()` rather than injected as fields.
   *
   * Injecting them eagerly closes a DI cycle and Angular throws NG0200:
   *   AuthSessionRedirectService -> PlatformNotificationService -> AuthSessionRedirectService
   * (RbacLiveSyncService also depends on PlatformNotificationService). They are only needed
   * when tearing a session down, so deferring resolution to call time breaks the cycle.
   */
  private readonly injector = inject(Injector);
  private redirecting = false;

  redirectToLogin(returnUrl?: string): void {
    if (globalThis.window === undefined) return;
    const path = this.router.url.split('?')[0];
    if (path.startsWith('/auth')) return;
    if (this.redirecting) return;

    this.redirecting = true;
    this.injector.get(RbacLiveSyncService).stop();
    this.injector.get(PlatformNotificationService).disconnect();
    this.profile.clearSession();
    this.permissionService.clearPermissions();

    const url = returnUrl ?? this.router.url;
    const queryParams =
      url && url !== '/' && !url.startsWith('/auth') ? { returnUrl: url } : undefined;

    void this.router
      .navigate(['/auth/signin'], { replaceUrl: true, queryParams })
      .finally(() => {
        globalThis.setTimeout(() => {
          this.redirecting = false;
        }, 400);
      });
  }
}
